import styled, { css } from 'styled-components';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { WidgetBackground } from '@elfsight/widget-ui';
import { useIsMobileBreakpoint } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../meta';
import type { PollSettings } from '../types';
import { ViewType } from '../types';
import { AnswersContainer, EmptyMessage, SuccessContainer } from './components';
import { useAnimationContext, useVoteContext } from './providers';

export function Widget() {
  const {
    renderThumbnailMode,
    background,
    textColor,
    question,
    answers,
    ...settings
  } = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);

  const { viewType } = useVoteContext();
  const { areAllAnimationsCompleted } = useAnimationContext();
  const isMobile = useIsMobileBreakpoint(430);

  const hasContent = question || answers.length > 0;

  return (
    <Deactivation2 {...settings} appAlias={meta.alias}>
      <StyledWidgetBackground
        {...background}
        _renderThumbnailMode={renderThumbnailMode}
        borderRadius={20}
        horizontalPadding={isMobile ? 24 : 40}
        verticalPadding={isMobile ? 24 : 40}
        contentStyle={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}
      >
        {hasContent ? (
          <>
            {question && (
              <Question role="heading" aria-level={2} _textColor={textColor}>
                {question}
              </Question>
            )}
            <AnswersContainer />
            {areAllAnimationsCompleted && viewType === ViewType.ResultView && (
              <SuccessContainer />
            )}
          </>
        ) : (
          <EmptyMessage textColor={textColor} />
        )}

        <FreeLink
          {...freeLinkSettings}
          appAlias={meta.alias}
          appName={meta.name}
          style={{
            link: {
              margin: '0',
              width: 'fit-content'
            }
          }}
        />
      </StyledWidgetBackground>
    </Deactivation2>
  );
}

const StyledWidgetBackground = styled(WidgetBackground)<{
  _renderThumbnailMode?: boolean;
}>`
  ${({ _renderThumbnailMode }) =>
    _renderThumbnailMode &&
    css`
      box-shadow: 0px 6px 50px rgba(0, 0, 0, 0.1);
    `}

  ${({ _renderThumbnailMode }) =>
    !_renderThumbnailMode &&
    css`
      transition: opacity 100ms ease;

      @starting-style {
        opacity: 0;
      }
    `}
`;

const Question = styled.div<{
  _textColor: PollSettings['textColor'];
}>`
  font-size: 22px;
  line-height: 1.27273;
  font-weight: 700;
  color: ${({ _textColor }) => _textColor};
`;
